import { Provider, IAgentRuntime, Memory, State, elizaLogger, ProviderResult } from '@elizaos/core';
import {
  ZapperConfig,
  TokenMarketData,
  MarketMetrics,
  HistoricalPriceData,
  TimeFrame,
  DataInterval,
  PriceChartData,
  TechnicalIndicators,
  VolumeData,
  LiquidityData,
} from '../types';
import { fetchWithPolyfill } from '../utils/fetchWithPolyfill';

export const marketDataProvider: Provider = {
  name: 'marketData',
  description:
    'Provides comprehensive market data, analytics, and trends for tokens and overall market metrics',
  get: async (runtime: IAgentRuntime, message: Memory, state: State): Promise<ProviderResult> => {
    elizaLogger.debug('Getting market data from provider');

    const hasApiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!hasApiKey) {
      elizaLogger.error('ZAPPER_API_KEY not configured');
      return {
        text: 'Market data provider requires ZAPPER_API_KEY configuration.',
        data: { error: 'API key not configured' },
      };
    }

    const zapperConfig: ZapperConfig = {
      apiKey: runtime.getSetting('ZAPPER_API_KEY'),
      apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
    };

    const text = message.content.text?.toLowerCase() || '';

    // Extract potential symbols from the message
    const symbols = extractSymbolsFromText(text);

    // Determine what type of market data to fetch
    const requestType = determineRequestType(text);

    try {
      let responseText = '📊 **Market Data Analytics**\n\n';
      let responseData: any = {};

      switch (requestType) {
        case 'overview':
          const marketOverview = await getMarketOverview(zapperConfig);
          responseText += formatMarketOverview(marketOverview);
          responseData.marketOverview = marketOverview;
          break;

        case 'token':
          if (symbols.length > 0) {
            const tokenData = await getTokenMarketData(zapperConfig, symbols[0]);
            responseText += formatTokenMarketData(tokenData);
            responseData.tokenData = tokenData;
          } else {
            responseText += 'Please specify a token symbol for market data.';
          }
          break;

        case 'trending':
          const trendingData = await getTrendingTokens(zapperConfig);
          responseText += formatTrendingTokens(trendingData);
          responseData.trendingData = trendingData;
          break;

        case 'volume':
          const volumeData = await getVolumeAnalytics(zapperConfig);
          responseText += formatVolumeAnalytics(volumeData);
          responseData.volumeData = volumeData;
          break;

        case 'liquidity':
          const liquidityData = await getLiquidityAnalytics(zapperConfig);
          responseText += formatLiquidityAnalytics(liquidityData);
          responseData.liquidityData = liquidityData;
          break;

        case 'performance':
          const performanceData = await getPerformanceAnalytics(zapperConfig, symbols);
          responseText += formatPerformanceAnalytics(performanceData);
          responseData.performanceData = performanceData;
          break;

        default:
          // Default to general market context
          const contextData = await getMarketContext(zapperConfig, symbols);
          responseText += formatMarketContext(contextData);
          responseData.contextData = contextData;
          break;
      }

      return {
        text: responseText,
        data: responseData,
      };
    } catch (error) {
      elizaLogger.error('marketDataProvider: Error fetching market data:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        apiUrl: zapperConfig.apiUrl,
        hasApiKey: !!zapperConfig.apiKey,
        requestType: requestType,
      });
      return {
        text: 'Unable to fetch market data at this time. Please try again later.',
        data: {
          provider: 'marketDataProvider',
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
          requestType: requestType,
        },
      };
    }
  },
};

function extractSymbolsFromText(text: string): string[] {
  const symbols: string[] = [];

  // Common crypto symbols
  const cryptoSymbols = [
    'BTC',
    'ETH',
    'USDC',
    'USDT',
    'BNB',
    'ADA',
    'DOT',
    'SOL',
    'MATIC',
    'AVAX',
    'LINK',
    'UNI',
  ];

  for (const symbol of cryptoSymbols) {
    if (text.includes(symbol.toLowerCase()) || text.includes(symbol)) {
      symbols.push(symbol);
    }
  }

  // Extract potential symbols from patterns like $SYMBOL or SYMBOL/USD or SYMBOL token/price data
  const symbolMatches = text.match(
    /\$([A-Z]{2,15})|([A-Z]{2,15})\/USD|([A-Z]{2,15})\s+(?:token|price|data|chart|market)/gi
  );
  if (symbolMatches) {
    for (const match of symbolMatches) {
      const symbol = match.replace(/[\$\/USD\s].*$/gi, '').toUpperCase();
      // Only exclude exact generic words, not longer tokens like "INVALIDTOKEN"
      const isGenericWord = [
        'TOKEN',
        'PRICE',
        'DATA',
        'CHART',
        'MARKET',
        'SHOW',
        'GET',
        'FETCH',
        'ME',
      ].includes(symbol);
      const isValidSymbol = symbol.length >= 2 && symbol.length <= 15 && !isGenericWord;

      if (isValidSymbol) {
        symbols.push(symbol);
      }
    }
  }

  return Array.from(new Set(symbols)); // Remove duplicates
}

function determineRequestType(text: string): string {
  if (
    text.includes('overview') ||
    text.includes('market summary') ||
    text.includes('market status')
  ) {
    return 'overview';
  }
  if (text.includes('trending') || text.includes('top gainers') || text.includes('top losers')) {
    return 'trending';
  }
  if (text.includes('volume') || text.includes('trading volume')) {
    return 'volume';
  }
  if (text.includes('liquidity') || text.includes('pool') || text.includes('tvl')) {
    return 'liquidity';
  }
  if (text.includes('performance') || text.includes('returns') || text.includes('analytics')) {
    return 'performance';
  }
  if (text.includes('price') || text.includes('market cap') || text.includes('token')) {
    return 'token';
  }
  return 'context';
}

async function getMarketOverview(config: ZapperConfig): Promise<MarketMetrics> {
  try {
    // Get major tokens data for market overview
    const majorTokens = [
      { address: '0x0000000000000000000000000000000000000000', chainId: 1 }, // ETH
      { address: '0xdac17f958d2ee523a2206206994597c13d831ec7', chainId: 1 }, // USDT
      { address: '0xa0b86a33e6cd31b9d3bb8fa3cb97b4f8b3c1b1f0', chainId: 1 }, // USDC
      { address: '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984', chainId: 1 }, // UNI
      { address: '0x7fc66500c84a76ad7e9c93437bfc5ac33e2ddae9', chainId: 1 }, // AAVE
    ];

    const query = `
      query GetMarketOverview($tokens: [FungibleTokenInputV2!]!) {
        fungibleTokenBatchV2(tokens: $tokens) {
          id
          address
          name
          symbol
          decimals
          networkId
          imageUrlV2
          priceData {
            price
            marketCap
            totalLiquidity
            priceChange24h
            volume24h
          }
        }
      }
    `;

    const response = await fetchWithPolyfill(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables: { tokens: majorTokens },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      elizaLogger.error('marketDataProvider: HTTP error in getMarketOverview:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result: any = await response.json();

    if (result.errors) {
      elizaLogger.error('marketDataProvider: GraphQL errors in getMarketOverview:', {
        errors: result.errors,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const tokens: any[] = result.data?.fungibleTokenBatchV2?.filter(Boolean) || [];
    
    // Calculate market metrics
    const totalMarketCap = tokens.reduce((sum: number, token: any) => sum + (token.priceData?.marketCap || 0), 0);
    const totalVolume24h = tokens.reduce((sum: number, token: any) => sum + (token.priceData?.volume24h || 0), 0);
    const defiTVL = tokens.reduce((sum: number, token: any) => sum + (token.priceData?.totalLiquidity || 0), 0);

    // Sort tokens by price change for gainers/losers
    const sortedByChange = tokens
      .filter((token: any) => token.priceData?.priceChange24h !== undefined)
      .sort((a: any, b: any) => (b.priceData?.priceChange24h || 0) - (a.priceData?.priceChange24h || 0));

    const topGainers = sortedByChange.slice(0, 3).map((token: any) => ({
      symbol: token.symbol,
      name: token.name,
      currentPrice: token.priceData?.price || 0,
      priceChange24h: token.priceData?.priceChange24h || 0,
      priceChangePercent24h: token.priceData?.priceChange24h || 0,
      marketCap: token.priceData?.marketCap || 0,
      volume24h: token.priceData?.volume24h || 0,
      address: token.address,
      network: 'ethereum',
      currentPriceUSD: token.priceData?.price || 0,
      circulatingSupply: token.priceData?.marketCap && token.priceData?.price ? 
        (token.priceData.marketCap / token.priceData.price) : 0,
      totalSupply: token.priceData?.marketCap && token.priceData?.price ? 
        (token.priceData.marketCap / token.priceData.price) : 0,
      maxSupply: undefined,
      lastUpdated: new Date().toISOString(),
    }));

    const topLosers = sortedByChange.slice(-3).reverse().map((token: any) => ({
      symbol: token.symbol,
      name: token.name,
      currentPrice: token.priceData?.price || 0,
      priceChange24h: token.priceData?.priceChange24h || 0,
      priceChangePercent24h: token.priceData?.priceChange24h || 0,
      marketCap: token.priceData?.marketCap || 0,
      volume24h: token.priceData?.volume24h || 0,
      address: token.address,
      network: 'ethereum',
      currentPriceUSD: token.priceData?.price || 0,
      circulatingSupply: token.priceData?.marketCap && token.priceData?.price ? 
        (token.priceData.marketCap / token.priceData.price) : 0,
      totalSupply: token.priceData?.marketCap && token.priceData?.price ? 
        (token.priceData.marketCap / token.priceData.price) : 0,
      maxSupply: undefined,
      lastUpdated: new Date().toISOString(),
    }));

    return {
      totalMarketCap,
      totalVolume24h,
      defiTVL,
      btcDominance: 0, // Not available in current API
      ethDominance: 0, // Not available in current API
      activeTokens: tokens.length,
      topGainers,
      topLosers,
      trending: sortedByChange.slice(0, 5).map((token: any) => ({
        symbol: token.symbol,
        name: token.name,
        currentPrice: token.priceData?.price || 0,
        priceChange24h: token.priceData?.priceChange24h || 0,
        priceChangePercent24h: token.priceData?.priceChange24h || 0,
        marketCap: token.priceData?.marketCap || 0,
        volume24h: token.priceData?.volume24h || 0,
        address: token.address,
        network: 'ethereum',
        currentPriceUSD: token.priceData?.price || 0,
        circulatingSupply: token.priceData?.marketCap && token.priceData?.price ? 
          (token.priceData.marketCap / token.priceData.price) : 0,
        totalSupply: token.priceData?.marketCap && token.priceData?.price ? 
          (token.priceData.marketCap / token.priceData.price) : 0,
        maxSupply: undefined,
        lastUpdated: new Date().toISOString(),
      })),
      newListings: [],
    };
  } catch (error) {
    elizaLogger.error('marketDataProvider: Error in getMarketOverview:', {
      error: error instanceof Error ? error.message : String(error),
    });
    return createDefaultMarketMetrics();
  }
}

async function getTokenMarketData(
  config: ZapperConfig,
  symbol: string
): Promise<TokenMarketData | null> {
  try {
    // First, search for the token by symbol
    const searchQuery = `
      query SearchTokens($input: SearchInputV2!) {
        searchV2(input: $input) {
          results {
            ... on UnifiedErc20TokenResult {
              id
              title
              imageUrl
              symbol
              name
              groupedFungibleTokens {
                id
                address
                name
                symbol
                decimals
                networkId
                imageUrlV2
                priceData {
                  price
                  marketCap
                  totalLiquidity
                  priceChange24h
                  volume24h
                }
              }
            }
            ... on Erc20TokenResult {
              id
              title
              imageUrl
              symbol
              name
              network
              erc20 {
                id
                address
                name
                symbol
                decimals
                networkId
                imageUrlV2
                priceData {
                  price
                  marketCap
                  totalLiquidity
                  priceChange24h
                  volume24h
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetchWithPolyfill(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query: searchQuery,
        variables: {
          input: {
            search: symbol,
            maxResultsPerCategory: 1,
            categories: ['ERC20_TOKEN', 'UNIFIED_ERC20_TOKEN'],
            chainIds: [1], // Ethereum mainnet
          },
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      elizaLogger.error('marketDataProvider: HTTP error in getTokenMarketData:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        symbol,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result: any = await response.json();

    if (result.errors) {
      elizaLogger.error('marketDataProvider: GraphQL errors in getTokenMarketData:', {
        errors: result.errors,
        symbol,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const results: any[] = result.data?.searchV2?.results || [];
    
    // Find the best matching token
    let matchingToken = null;
    for (const searchResult of results) {
      if (searchResult.groupedFungibleTokens) {
        // Handle UnifiedErc20TokenResult
        const token = searchResult.groupedFungibleTokens.find(
          (t: any) => t.symbol.toLowerCase() === symbol.toLowerCase()
        );
        if (token) {
          matchingToken = token;
          break;
        }
      } else if (searchResult.erc20) {
        // Handle Erc20TokenResult
        const token = searchResult.erc20;
        if (token.symbol.toLowerCase() === symbol.toLowerCase()) {
          matchingToken = token;
          break;
        }
      }
    }

    if (!matchingToken) {
      elizaLogger.warn('marketDataProvider: No matching token found for symbol:', symbol);
      return null;
    }

    // Convert to TokenMarketData format
    return {
      symbol: matchingToken.symbol,
      name: matchingToken.name,
      address: matchingToken.address,
      currentPrice: matchingToken.priceData?.price || 0,
      marketCap: matchingToken.priceData?.marketCap || 0,
      volume24h: matchingToken.priceData?.volume24h || 0,
      priceChange24h: matchingToken.priceData?.priceChange24h || 0,
      priceChangePercent24h: matchingToken.priceData?.priceChange24h || 0,
      circulatingSupply: matchingToken.priceData?.marketCap && matchingToken.priceData?.price ? 
        (matchingToken.priceData.marketCap / matchingToken.priceData.price) : 0,
      totalSupply: matchingToken.priceData?.marketCap && matchingToken.priceData?.price ? 
        (matchingToken.priceData.marketCap / matchingToken.priceData.price) : 0,
      maxSupply: undefined,
      lastUpdated: new Date().toISOString(),
      network: 'ethereum',
      currentPriceUSD: matchingToken.priceData?.price || 0,
    };
  } catch (error) {
    elizaLogger.error('marketDataProvider: Error in getTokenMarketData:', {
      error: error instanceof Error ? error.message : String(error),
      symbol,
    });
    return null;
  }
}

async function getTrendingTokens(config: ZapperConfig): Promise<TokenMarketData[]> {
  try {
    // Get a selection of popular tokens that typically have good volume
    const popularTokens = [
      { address: '0x0000000000000000000000000000000000000000', chainId: 1 }, // ETH
      { address: '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984', chainId: 1 }, // UNI
      { address: '0x7fc66500c84a76ad7e9c93437bfc5ac33e2ddae9', chainId: 1 }, // AAVE
      { address: '0x514910771af9ca656af840dff83e8264ecf986ca', chainId: 1 }, // LINK
      { address: '0x6b175474e89094c44da98b954eedeac495271d0f', chainId: 1 }, // DAI
      { address: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', chainId: 1 }, // WBTC
    ];

    const query = `
      query GetTrendingTokens($tokens: [FungibleTokenInputV2!]!) {
        fungibleTokenBatchV2(tokens: $tokens) {
          id
          address
          name
          symbol
          decimals
          networkId
          imageUrlV2
          priceData {
            price
            marketCap
            totalLiquidity
            priceChange24h
            volume24h
          }
        }
      }
    `;

    const response = await fetchWithPolyfill(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables: { tokens: popularTokens },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      elizaLogger.error('marketDataProvider: HTTP error in getTrendingTokens:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result: any = await response.json();

    if (result.errors) {
      elizaLogger.error('marketDataProvider: GraphQL errors in getTrendingTokens:', {
        errors: result.errors,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const tokens: any[] = result.data?.fungibleTokenBatchV2?.filter(Boolean) || [];
    
    // Sort by volume (trending by activity)
    const sortedTokens = tokens
      .filter((token: any) => token.priceData?.volume24h)
      .sort((a: any, b: any) => (b.priceData?.volume24h || 0) - (a.priceData?.volume24h || 0));

    return sortedTokens.map((token: any) => ({
      symbol: token.symbol,
      name: token.name,
      currentPrice: token.priceData?.price || 0,
      priceChange24h: token.priceData?.priceChange24h || 0,
      priceChangePercent24h: token.priceData?.priceChange24h || 0,
      marketCap: token.priceData?.marketCap || 0,
      volume24h: token.priceData?.volume24h || 0,
      address: token.address,
      circulatingSupply: token.priceData?.marketCap && token.priceData?.price ? 
        (token.priceData.marketCap / token.priceData.price) : 0,
      totalSupply: token.priceData?.marketCap && token.priceData?.price ? 
        (token.priceData.marketCap / token.priceData.price) : 0,
      maxSupply: undefined,
      lastUpdated: new Date().toISOString(),
      network: 'ethereum',
      currentPriceUSD: token.priceData?.price || 0,
    }));
  } catch (error) {
    elizaLogger.error('marketDataProvider: Error in getTrendingTokens:', {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}

async function getVolumeAnalytics(config: ZapperConfig): Promise<VolumeData[]> {
  try {
    // Use the same trending tokens approach to get volume analytics
    const popularTokens = [
      { address: '0x0000000000000000000000000000000000000000', chainId: 1 }, // ETH
      { address: '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984', chainId: 1 }, // UNI
      { address: '0xdac17f958d2ee523a2206206994597c13d831ec7', chainId: 1 }, // USDT
      { address: '0xa0b86a33e6cd31b9d3bb8fa3cb97b4f8b3c1b1f0', chainId: 1 }, // USDC
      { address: '0x7fc66500c84a76ad7e9c93437bfc5ac33e2ddae9', chainId: 1 }, // AAVE
    ];

    const query = `
      query GetVolumeAnalytics($tokens: [FungibleTokenInputV2!]!) {
        fungibleTokenBatchV2(tokens: $tokens) {
          id
          address
          name
          symbol
          decimals
          networkId
          imageUrlV2
          priceData {
            price
            marketCap
            totalLiquidity
            priceChange24h
            volume24h
          }
        }
      }
    `;

    const response = await fetchWithPolyfill(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables: { tokens: popularTokens },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      elizaLogger.error('marketDataProvider: HTTP error in getVolumeAnalytics:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result: any = await response.json();

    if (result.errors) {
      elizaLogger.error('marketDataProvider: GraphQL errors in getVolumeAnalytics:', {
        errors: result.errors,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const tokens: any[] = result.data?.fungibleTokenBatchV2?.filter(Boolean) || [];
    
    // Sort by volume and convert to VolumeData format
    const sortedTokens = tokens
      .filter((token: any) => token.priceData?.volume24h)
      .sort((a: any, b: any) => (b.priceData?.volume24h || 0) - (a.priceData?.volume24h || 0));

    return sortedTokens.map((token: any) => ({
      timestamp: new Date().toISOString(),
      volume: token.priceData?.volume24h || 0,
      volumeUSD: token.priceData?.volume24h || 0,
      transactionCount: Math.floor((token.priceData?.volume24h || 0) / (token.priceData?.price || 1)), // Approximate
      uniqueUsers: Math.floor((token.priceData?.volume24h || 0) / (token.priceData?.price || 1) / 10), // Approximate
      averageTransactionSize: token.priceData?.price || 0,
      symbol: token.symbol,
      name: token.name,
      currentPrice: token.priceData?.price || 0,
      priceChange24h: token.priceData?.priceChange24h || 0,
    }));
  } catch (error) {
    elizaLogger.error('marketDataProvider: Error in getVolumeAnalytics:', {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}

async function getLiquidityAnalytics(config: ZapperConfig): Promise<LiquidityData[]> {
  try {
    // Use the same tokens approach to get liquidity analytics from priceData.totalLiquidity
    const liquidityTokens = [
      { address: '0x0000000000000000000000000000000000000000', chainId: 1 }, // ETH
      { address: '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984', chainId: 1 }, // UNI
      { address: '0xdac17f958d2ee523a2206206994597c13d831ec7', chainId: 1 }, // USDT
      { address: '0xa0b86a33e6cd31b9d3bb8fa3cb97b4f8b3c1b1f0', chainId: 1 }, // USDC
      { address: '0x7fc66500c84a76ad7e9c93437bfc5ac33e2ddae9', chainId: 1 }, // AAVE
    ];

    const query = `
      query GetLiquidityAnalytics($tokens: [FungibleTokenInputV2!]!) {
        fungibleTokenBatchV2(tokens: $tokens) {
          id
          address
          name
          symbol
          decimals
          networkId
          imageUrlV2
          priceData {
            price
            marketCap
            totalLiquidity
            priceChange24h
            volume24h
          }
        }
      }
    `;

    const response = await fetchWithPolyfill(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables: { tokens: liquidityTokens },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      elizaLogger.error('marketDataProvider: HTTP error in getLiquidityAnalytics:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result: any = await response.json();

    if (result.errors) {
      elizaLogger.error('marketDataProvider: GraphQL errors in getLiquidityAnalytics:', {
        errors: result.errors,
      });
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    const tokens: any[] = result.data?.fungibleTokenBatchV2?.filter(Boolean) || [];
    
    // Sort by liquidity and convert to LiquidityData format
    const sortedTokens = tokens
      .filter((token: any) => token.priceData?.totalLiquidity)
      .sort((a: any, b: any) => (b.priceData?.totalLiquidity || 0) - (a.priceData?.totalLiquidity || 0));

    return sortedTokens.map((token: any) => ({
      pool: `${token.symbol}/USD`,
      tokenA: token.symbol,
      tokenB: 'USD', // Approximation - most liquidity is paired with USD/stablecoins
      protocol: 'Uniswap V3', // Approximation
      liquidityUSD: token.priceData?.totalLiquidity || 0,
      volume24h: token.priceData?.volume24h || 0,
      volume24hUSD: token.priceData?.volume24h || 0,
      apr: token.priceData?.volume24h && token.priceData?.totalLiquidity ? 
        (token.priceData.volume24h / token.priceData.totalLiquidity) * 365 * 100 : 0, // Rough APR approximation
      network: 'ethereum',
      timestamp: new Date().toISOString(),
    }));
  } catch (error) {
    elizaLogger.error('marketDataProvider: Error in getLiquidityAnalytics:', {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}

async function getPerformanceAnalytics(config: ZapperConfig, symbols: string[]): Promise<any> {
  try {
    if (symbols.length === 0) {
      return { message: 'No symbols provided for performance analysis' };
    }

    // Search for each symbol and get its performance data
    const performancePromises = symbols.map(symbol => getTokenMarketData(config, symbol));
    const performanceResults = await Promise.all(performancePromises);

    // Filter out null results and format for performance display
    const performanceData = performanceResults
      .filter(Boolean)
      .map((token: any) => ({
        symbol: token.symbol,
        name: token.name,
        currentPrice: token.currentPrice,
        priceChange24h: token.priceChange24h,
        priceChangePercent24h: token.priceChangePercent24h,
        volume24h: token.volume24h,
        marketCap: token.marketCap,
        address: token.address,
        network: token.network,
        lastUpdated: token.lastUpdated,
      }));

    return performanceData;
  } catch (error) {
    elizaLogger.error('marketDataProvider: Error in getPerformanceAnalytics:', {
      error: error instanceof Error ? error.message : String(error),
      symbols,
    });
    return [];
  }
}

async function getMarketContext(config: ZapperConfig, symbols: string[]): Promise<any> {
  // Provide general market context
  const marketData = await getMarketOverview(config);
  const contextData: any = { marketOverview: marketData };

  if (symbols.length > 0) {
    contextData.tokenData = await Promise.all(
      symbols.slice(0, 3).map((symbol) => getTokenMarketData(config, symbol))
    );
  }

  return contextData;
}

function formatMarketOverview(data: MarketMetrics): string {
  return `**📈 Market Overview**

💰 **Total Market Cap**: $${(data.totalMarketCap / 1e12).toFixed(2)}T
📊 **24h Volume**: $${(data.totalVolume24h / 1e9).toFixed(2)}B
🏦 **DeFi TVL**: $${(data.defiTVL / 1e9).toFixed(2)}B
₿ **BTC Dominance**: ${data.btcDominance?.toFixed(1)}%
⚡ **ETH Dominance**: ${data.ethDominance?.toFixed(1)}%
🔍 **Active Tokens**: ${data.activeTokens?.toLocaleString()}
😱 **Fear & Greed Index**: ${data.fearGreedIndex || 'N/A'}
🧠 **Market Sentiment**: ${formatSentiment(data.marketSentiment)}

**🚀 Top Gainers**:
${
  data.topGainers
    ?.slice(0, 3)
    .map(
      (token) =>
        `• ${token.symbol}: $${token.currentPrice?.toFixed(4)} (+${token.priceChangePercent24h?.toFixed(2)}%)`
    )
    .join('\n') || 'Data not available'
}

**📉 Top Losers**:
${
  data.topLosers
    ?.slice(0, 3)
    .map(
      (token) =>
        `• ${token.symbol}: $${token.currentPrice?.toFixed(4)} (${token.priceChangePercent24h?.toFixed(2)}%)`
    )
    .join('\n') || 'Data not available'
}`;
}

function formatTokenMarketData(data: TokenMarketData | null): string {
  if (!data) {
    return 'Token market data not available.';
  }

  return `**📊 ${data.symbol} Market Data**

💰 **Price**: $${data.currentPrice?.toFixed(4)}
📈 **24h Change**: ${data.priceChange24h !== undefined && data.priceChange24h >= 0 ? '+' : ''}$${data.priceChange24h?.toFixed(4) || 'N/A'} (${data.priceChangePercent24h?.toFixed(2) || 'N/A'}%)
📊 **Market Cap**: $${data.marketCap ? (data.marketCap / 1e9).toFixed(2) + 'B' : 'N/A'}
💹 **24h Volume**: $${data.volume24h ? (data.volume24h / 1e6).toFixed(2) + 'M' : 'N/A'}
🔄 **Circulating Supply**: ${data.circulatingSupply ? data.circulatingSupply.toLocaleString() : 'N/A'}
📅 **Last Updated**: ${data.lastUpdated ? new Date(data.lastUpdated).toLocaleString() : 'N/A'}`;
}

function formatTrendingTokens(data: TokenMarketData[]): string {
  if (data.length === 0) {
    return 'No trending tokens data available.';
  }

  return `**🔥 Trending Tokens**

${data
  .slice(0, 10)
  .map(
    (token, index) =>
      `${index + 1}. **${token.symbol}** - $${token.currentPrice?.toFixed(4)} (${token.priceChangePercent24h !== undefined && token.priceChangePercent24h >= 0 ? '+' : ''}${token.priceChangePercent24h?.toFixed(2) || 'N/A'}%)
   💹 Volume: $${token.volume24h ? (token.volume24h / 1e6).toFixed(2) + 'M' : 'N/A'}
   📊 Market Cap: $${token.marketCap ? (token.marketCap / 1e9).toFixed(2) + 'B' : 'N/A'}`
  )
  .join('\n\n')}`;
}

function formatVolumeAnalytics(data: VolumeData[]): string {
  if (data.length === 0) {
    return 'No volume analytics data available.';
  }

  return `**📊 Volume Analytics**

${data
  .slice(0, 10)
  .map(
    (item, index) =>
      `${index + 1}. **${item.symbol || 'Unknown'}** - $${item.currentPrice?.toFixed(4) || 'N/A'}
   💹 24h Volume: $${(item.volumeUSD / 1e6).toFixed(2)}M
   📈 Price Change: ${item.priceChange24h !== undefined && item.priceChange24h >= 0 ? '+' : ''}${(item.priceChange24h || 0).toFixed(2)}%`
  )
  .join('\n\n')}`;
}

function formatLiquidityAnalytics(data: LiquidityData[]): string {
  if (data.length === 0) {
    return 'No liquidity analytics data available.';
  }

  return `**🌊 Liquidity Analytics**

${data
  .slice(0, 10)
  .map(
    (pool, index) =>
      `${index + 1}. **${pool.tokenA}/${pool.tokenB}** (${pool.protocol})
   💰 Liquidity: $${(pool.liquidityUSD / 1e6).toFixed(2)}M
   💹 24h Volume: $${(pool.volume24hUSD / 1e6).toFixed(2)}M
   💎 APR: ${pool.apr?.toFixed(2)}%
   🌐 Network: ${pool.network}`
  )
  .join('\n\n')}`;
}

function formatPerformanceAnalytics(data: any[]): string {
  if (data.length === 0) {
    return 'No performance analytics data available.';
  }

  return `**🎯 Performance Analytics**

${data
  .map(
    (token) =>
      `**${token.symbol}** - $${token.currentPrice?.toFixed(4)}
   📈 24h: ${token.priceChangePercent24h !== undefined && token.priceChangePercent24h >= 0 ? '+' : ''}${token.priceChangePercent24h?.toFixed(2) || 'N/A'}%
   📊 Volume: $${token.volume24h ? (token.volume24h / 1e6).toFixed(2) + 'M' : 'N/A'}
   💰 Market Cap: $${token.marketCap ? (token.marketCap / 1e9).toFixed(2) + 'B' : 'N/A'}`
  )
  .join('\n\n')}`;
}

function formatMarketContext(data: any): string {
  let response = '';

  if (data.marketOverview) {
    response += formatMarketOverview(data.marketOverview);
  }

  if (data.tokenData && data.tokenData.length > 0) {
    response += '\n\n**🎯 Token Context**:\n\n';
    data.tokenData.forEach((token: TokenMarketData | null) => {
      if (token) {
        response += `• **${token.symbol}**: $${token.currentPrice?.toFixed(4)} (${token.priceChangePercent24h !== undefined && token.priceChangePercent24h >= 0 ? '+' : ''}${token.priceChangePercent24h?.toFixed(2) || 'N/A'}%)\n`;
      }
    });
  }

  return response;
}

function formatSentiment(sentiment?: string): string {
  if (!sentiment) return 'Neutral';

  const sentimentMap: { [key: string]: string } = {
    extremely_fearful: '😱 Extremely Fearful',
    fearful: '😰 Fearful',
    neutral: '😐 Neutral',
    greedy: '😊 Greedy',
    extremely_greedy: '🤑 Extremely Greedy',
  };

  return sentimentMap[sentiment] || sentiment;
}

function createDefaultMarketMetrics(): MarketMetrics {
  return {
    totalMarketCap: 0,
    totalVolume24h: 0,
    defiTVL: 0,
    btcDominance: 0,
    ethDominance: 0,
    activeTokens: 0,
    topGainers: [],
    topLosers: [],
    trending: [],
    newListings: [],
  };
}
